import React, {useEffect, useState} from 'react';
import authManager, {AUTH_STATE_CHANGE, PERMISSIONS} from './lib/authManager';
import compose from 'lodash/flowRight';
import { PermissionRoute } from './components/AdminOnly';
import {Helmet} from 'react-helmet';
import {LoadingFork} from './components/LoadingFork';
import {PageFooter, StartContainer} from './components/Container';
import {PoweredBy} from './components/PoweredBy'
import {setDefaultTimezone} from './lib/momentConfig';
import {withAppConfig} from './components/AppConfigContext';
import {withInstallation} from './components/InstallationContext';
import {Redirect, Route, Switch, withRouter} from 'react-router-dom';
import BookingNewPage from './pages/Booking/New';
import BookingPage from './pages/Booking';
import LoginPage from './pages/Login';
import ProfilePage from './pages/Profile';
import SettingsPage from './pages/Settings';
import HealthPage from './pages/Health';
import StreamsPage from "./pages/Booking/StreamsPage";
import SupportPage from "./pages/Support";
import Dashboard from "./pages/Dashboard";
import DashboardV2 from "./pages/DashboardV2";

import SideBar from './components/Sidebar'
import TopBar from './components/TopBar';
import UpcomingExtras from "./pages/Booking/UpcomingExtras";
import QrCodesPage from "./pages/QrCodes";
import ParkingOperationsPage from "./pages/ParkingOperations";
import ReportsPage from "./pages/Reports";
import InsightsPage from "./pages/Insights";
import ConciergePage from "./pages/Concierge";
import RunnerApp from "./pages/Concierge/runner";
import {INSIGHTS_PAGE_PERMISSIONS} from './lib/insightsPermissions';

function PrivateRoute({component: Component, ...rest}) {
    return <Route {...rest} render={(props) => <Component {...props} />}/>;
}

const SETTINGS_PERMISSIONS = [
    PERMISSIONS.GUEST_CARDS_READ,
    PERMISSIONS.GUEST_CARDS_MANAGE,
    PERMISSIONS.PARKING_LOTS_READ,
    PERMISSIONS.PARKING_LOTS_MANAGE,
    PERMISSIONS.PAYOUTS_READ,
    PERMISSIONS.PAYOUTS_MANAGE,
    PERMISSIONS.SANCTIONED_TIMES_READ,
    PERMISSIONS.SANCTIONED_TIMES_MANAGE,
    PERMISSIONS.USERS_READ,
    PERMISSIONS.USERS_MANAGE,
    PERMISSIONS.SANCTIONED_PLATES_READ,
    PERMISSIONS.SANCTIONED_PLATES_MANAGE,
];

const REPORTS_PERMISSIONS = [
    PERMISSIONS.REPORTS_EXPORT,
    PERMISSIONS.TRANSACTIONS_READ,
];

function App(props) {
    const {installation, match, appConfig} = props
    const [authenticated, setAuthenticated] = useState(authManager.isAuthenticated)
    const [updating, setUpdating] = useState(authManager.isUpdating)
    const [statusMessage, setStatus] = useState(authManager.statusMessage)
    const isSidebarCollapsed = false;
    const setIsSidebarCollapsed = () => {};

    function onAuthStateChange(authState) {
        setAuthenticated(authManager.isAuthenticated)
        setUpdating(authManager.isUpdating)
        setStatus(authState.statusMessage)
    }

    useEffect(() => {
        try {
            setDefaultTimezone(installation.timeZoneName);
        } catch (err) {
            console.error(err); // eslint-disable-line no-console
        }

        authManager.addListener(AUTH_STATE_CHANGE, onAuthStateChange);
        return () => {
            authManager.removeListener(AUTH_STATE_CHANGE, onAuthStateChange);
        }
    }, [installation])

    if (authenticated) {
        return (
            <>
                <Helmet defaultTitle="Hotel 101" titleTemplate="%s | Hotel 101">
                    {installation && <title>{installation.name}</title>}
                </Helmet>
                <Switch>
                    <Route path={match.path} render={() => (
                        <div style={{display: 'flex', height: '100vh'}}>
                            <SideBar {...props} isCollapsed={isSidebarCollapsed} setIsCollapsed={setIsSidebarCollapsed} />
                            <div style={{flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
                                <TopBar installation={installation} appConfig={appConfig}/>
                                <main style={{flex: 1, padding: '20px', overflow: 'auto'}}>
                                    <Switch>
                                        <PermissionRoute
                                            exact
                                            path={`${match.path}/runner`}
                                            component={RunnerApp}
                                            permissions={PERMISSIONS.RUNNER_QUEUE_MANAGE}
                                        />
                                        <PermissionRoute
                                            exact
                                            path={`${match.path}/dashboard`}
                                            component={Dashboard}
                                            permissions={PERMISSIONS.DASHBOARD_READ}
                                        />
                                        <PermissionRoute
                                            exact
                                            path={`${match.path}/dashboard-v2`}
                                            component={DashboardV2}
                                            permissions={PERMISSIONS.DASHBOARD_READ}
                                        />
                                        <PermissionRoute
                                            exact
                                            path={`${match.path}/new-booking`}
                                            component={BookingNewPage}
                                            permissions={PERMISSIONS.HOME_READ}
                                        />
                                        <PermissionRoute
                                            exact
                                            path={`${match.path}/upcoming-extras`}
                                            component={UpcomingExtras}
                                            permissions={PERMISSIONS.PARKING_UPCOMING_READ}
                                        />
                                        <PermissionRoute
                                            exact
                                            path={`${match.path}/streams`}
                                            component={StreamsPage}
                                            permissions={PERMISSIONS.CAMERAS_READ}
                                        />
                                        <PermissionRoute
                                            exact
                                            path={`${match.path}/parking-operations`}
                                            component={ParkingOperationsPage}
                                            permissions={[PERMISSIONS.PARKING_OPERATIONS_READ, PERMISSIONS.PARKING_OPERATIONS_MANAGE]}
                                        />
                                        <PermissionRoute
                                            path={`${match.path}/bookings`}
                                            component={BookingPage}
                                            permissions={PERMISSIONS.BOOKINGS_READ}
                                        />
                                        <PermissionRoute
                                            path={`${match.path}/qr-codes`}
                                            component={QrCodesPage}
                                            permissions={PERMISSIONS.BOOKINGS_READ}
                                        />
                                        <PrivateRoute
                                            path={`${match.path}/profile`}
                                            component={ProfilePage}
                                        />
                                        <PermissionRoute
                                            path={`${match.path}/support`}
                                            component={SupportPage}
                                            permissions={PERMISSIONS.SUPPORT_READ}
                                        />
                                        <PermissionRoute
                                            path={`${match.path}/reports`}
                                            component={ReportsPage}
                                            permissions={REPORTS_PERMISSIONS}
                                        />
                                        <PermissionRoute
                                            path={`${match.path}/insights`}
                                            component={InsightsPage}
                                            permissions={INSIGHTS_PAGE_PERMISSIONS}
                                        />
                                        <PermissionRoute
                                            path={`${match.path}/concierge`}
                                            component={ConciergePage}
                                            permissions={PERMISSIONS.DASHBOARD_READ}
                                        />
                                        <PermissionRoute
                                            path={`${match.path}/settings`}
                                            component={SettingsPage}
                                            permissions={SETTINGS_PERMISSIONS}
                                        />
                                        <Route exact path={`${match.path}/login`}>
                                            <Redirect to={authManager.landingPathFor(installation)}/>
                                        </Route>
                                        <Route path={`${match.path}/health`} component={HealthPage}/>
                                    </Switch>
                                    <div style={{textAlign: 'center', padding: '20px 0'}}>
                                        <PoweredBy/>
                                    </div>
                                </main>
                            </div>
                        </div>
                    )}/>
                </Switch>
            </>
        );
    }

    // Show a spinner rather than the login screen when loading auth state.
    if (updating) {
        return <LoadingFork loading statusMessage={statusMessage}/>;
    }

    return <StartContainer>
        <Switch>
            <Route path={`${match.path}/login`} component={LoginPage}/>
            <Redirect from={`${match.path}`} to={`${match.url}/login`}/>
        </Switch>
        <PageFooter>
            <PoweredBy/>
        </PageFooter>
    </StartContainer>
}

export default compose(
    withAppConfig,
    withInstallation,
    withRouter,
)(App)
